import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXPORT_FPS = 15;

export default function ExportVideoDialog({
  open,
  onOpenChange,
  fileId,
  fileName,
  sliceIndex = 0,
}) {
  const safeSlice = Math.max(0, sliceIndex ?? 0);
  const [format, setFormat] = useState("mp4");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setFormat("mp4");
  }, [open]);

  const handleExport = async () => {
    if (!fileId) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/dicom/${fileId}/export-video`,
        {
          format,
          fps: EXPORT_FPS,
          slice_index: safeSlice,
        },
        { responseType: "blob" }
      );
      const mime = format === "mp4" ? "video/mp4" : "video/x-msvideo";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const base = (fileName || "file").replace(/\.dcm$/i, "");
      a.download = `${base}_slice${safeSlice}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported slice ${safeSlice + 1} → ${format.toUpperCase()}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="export-video-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="w-4 h-4 text-primary" /> Export as video
          </DialogTitle>
          <DialogDescription className="text-xs">
            Encode the current slice as a standard video file. Original DICOM is untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Format</Label>
            <div className="flex gap-2">
              {[
                { v: "mp4", label: "MP4 (H.264)", note: "Best web compatibility" },
                { v: "avi", label: "AVI (MJPEG)", note: "Better for clinical PACS" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFormat(opt.v)}
                  data-testid={`format-${opt.v}`}
                  className={`flex-1 text-left p-2.5 rounded-md border text-xs transition-colors ${
                    format === opt.v
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border hover:bg-secondary/40 text-muted-foreground"
                  }`}
                >
                  <div className="font-semibold">{opt.label}</div>
                  <div className="text-[10px] mt-0.5 opacity-70">{opt.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between text-[11px] text-muted-foreground border-t pt-3">
            <span>Slice: <strong className="text-foreground">{safeSlice + 1}</strong></span>
            <span>FPS: <strong className="text-foreground">{EXPORT_FPS}</strong></span>
            <span>Format: <strong className="text-foreground uppercase">{format}</strong></span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="export-cancel">
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={busy} data-testid="export-confirm">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Encoding…</> : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
